import React, { useContext, useEffect, useRef, useState } from "react";
import { DraggableData } from "react-draggable";
import { ControllableShakaPlayer, Stack, useAsyncError } from "sprint-common-web";

import { AuthConfigContext } from "../../../../../../../core/configuration/auth/AuthConfigurationContext";
import { AppConfigContext } from "../../../../../../../core/configuration/Configuration";
import { AuthContext } from "../../../../../../../core/security/AuthProvider";
import { PreviewLoadingError } from "../PreviewLoadingError";
import { useVideoRekognitionResultContext } from "../rekognition/hooks/useVideoRekognitionResultContext";
import { RekognitionResultListing } from "../rekognition/listing/RekognitionResultListing";
import { useVideoPreviewContext } from "./hooks/useVideoPreviewContext";
import { VideoProgressBar } from "./rekognition-preview-elements/VideoProgressBar";
import { VideoTimeline } from "./rekognition-preview-elements/VideoTimeline";
import { VideoTimestampBar } from "./rekognition-preview-elements/VideoTimestampBar";

export interface RekognitionVideoPreviewProps {
    manifestUrl: string;
    cloudFileId: string;
}

export const RekognitionVideoPreview = ({
    manifestUrl: url,
    cloudFileId
}: RekognitionVideoPreviewProps): JSX.Element => {
    const containerRef = useRef<HTMLDivElement>(null);

    const {
        rewindTimestamp,
        currentTimestamp,
        videoDuration,
        setRewindTimestamp,
        setCurrentTimestamp,
        setVideoDuration
    } = useVideoPreviewContext();

    const {
        selectedItems: selectedRekognitionItems,
        isLoading: areRekognitionItemsLoading
    } = useVideoRekognitionResultContext();
    const appConfigContext = useContext(AppConfigContext);
    const authContext = useContext(AuthContext);
    const authConfigContext = useContext(AuthConfigContext);
    const throwError = useAsyncError();

    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [videoHeight, setVideoHeight] = useState<number>(0);
    const [progressBarFill, setProgressBarFill] = useState<number>(0);

    useEffect(() => {
        setWidth();
        window.addEventListener("resize", setWidth);
    }, []);

    useEffect(() => {
        const fillPercentage = (currentTimestamp / videoDuration) * 100;
        setProgressBarFill(fillPercentage);
    }, [currentTimestamp]);

    const setWidth = () => {
        if (containerRef.current) {
            setContainerWidth(containerRef.current.getBoundingClientRect().width);
        }
    };

    const handleDrag = (data: DraggableData) => {
        const calculatedTimestamp = (data.x / containerWidth) * videoDuration;
        setRewindTimestamp(calculatedTimestamp);
    };

    const handleError = () => {
        throwError(new PreviewLoadingError());
    };

    const widthPercentage = 55;
    const timestampBarHeight = 30;
    const progressBarHeight = 5;

    const userTokens = {
        accessToken: authContext.sprintUser.accessToken,
        appClientId: authConfigContext.clientId,
        cloudFileId,
        environment: appConfigContext.environment
    };

    const licenceCustomData = btoa(JSON.stringify(userTokens));

    return (
        <Stack horizontal={true}>
            <div ref={containerRef} style={{ position: "relative", width: `${widthPercentage}%` }}>
                <VideoTimeline
                    height={videoHeight + timestampBarHeight + progressBarHeight}
                    xBoundary={containerWidth}
                    xPosition={(currentTimestamp / videoDuration) * containerWidth}
                    onDrag={handleDrag}
                />
                <VideoTimestampBar
                    height={timestampBarHeight}
                    setRewindTimestamp={setRewindTimestamp}
                    videoDuration={videoDuration}
                    rekognitionItems={selectedRekognitionItems}
                    containerWidth={containerWidth}
                    isLoading={areRekognitionItemsLoading}
                />
                <ControllableShakaPlayer
                    setVideoHeight={setVideoHeight}
                    manifestUrl={url}
                    onError={handleError}
                    licenceCustomData={licenceCustomData}
                    licenceServer={appConfigContext.masterConfig.drmLicenceServer}
                    accessToken={authContext.sprintUser.accessToken}
                    rewindTimestamp={rewindTimestamp}
                    setCurrentTimestamp={setCurrentTimestamp}
                    setVideoDuration={setVideoDuration}
                />
                <VideoProgressBar height={progressBarHeight} fillPercentage={progressBarFill} />
            </div>
            <Stack style={{ width: `${100 - widthPercentage}%` }}>
                <RekognitionResultListing />
            </Stack>
        </Stack>
    );
};
